import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card } from '@mui/material';

import { EmptyBox } from '@europrocurement/flexy-components';
import {
    catalogIcon,
    dateInput,
    domainIcon,
    offerIcon,
    offerPackageIcon,
    originIcon,
} from '@europrocurement/l2d-icons';
import { useApiRequest } from '@europrocurement/l2d-hooks';
import { useOffreService } from '@europrocurement/l2d-domain';
import { CatalogueJsonldCatalogueRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';

import Form from './Form/Form';
import { catalogFichePath, catalogListPath } from './routes';
import { offerFichePath } from '../Offer/routes';
import { packageFichePath } from '../Package/routes';
import FicheHeader, { type FicheSubtitleElement } from '../components/fiche/FicheHeader';
import buildCreationTitle from '../components/fiche/buildCreationTitle';
import LoadingScreen from '../forms/LoadingScreen';

const CATALOG_SECTION_LABEL = 'Configuration des offres commerciales';

/** Formate une période `début → fin` à partir de dates ISO (la fin est optionnelle). */
const formatPeriod = (start?: string | null, end?: string | null): string | undefined => {
    const toFr = (value?: string | null): string | null => {
        if (!value) {
            return null;
        }
        const date = new Date(value);
        return Number.isNaN(date.getTime()) ? null : date.toLocaleDateString('fr-FR');
    };

    const startFr = toFr(start);
    const endFr = toFr(end);

    if (!startFr && !endFr) {
        return undefined;
    }

    return `${startFr ?? '…'} → ${endFr ?? '…'}`;
};

/**
 * Fiche d'un catalogue sur route dédiée (remplace l'édition/création en modale).
 * - `admin/offres/catalogue/new` : création.
 * - `admin/offres/catalogue/:id` : édition (le catalogue est chargé par son identifiant
 *   via le même endpoint que celui utilisé auparavant par la modale).
 */
const CatalogFiche: React.FunctionComponent = function () {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const isCreate = !id || id === 'new';

    const { catalogModel: model } = useOffreService();
    const { request } = useApiRequest<CatalogueJsonldCatalogueRead>();

    const [entity, setEntity] = useState<CatalogueJsonldCatalogueRead | undefined>(undefined);
    const [isLoading, setIsLoading] = useState<boolean>(!isCreate);
    const [isInError, setIsInError] = useState<boolean>(false);
    // Nom commercial saisi dans le formulaire (mode création), affiché dans l'en-tête.
    const [draftLabel, setDraftLabel] = useState<string>('');

    useEffect(() => {
        let active = true;
        if (!isCreate) {
            setIsLoading(true);
            setIsInError(false);

            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => {
                    if (active) {
                        setEntity(result.data);
                    }
                },
                failureCallback: () => {
                    if (active) {
                        setIsInError(true);
                    }
                },
            }).finally(() => {
                if (active) {
                    setIsLoading(false);
                }
            });
        }
        return () => {
            active = false;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id, isCreate]);

    const goToList = useCallback(() => {
        navigate(catalogListPath);
    }, [navigate]);

    // Après un enregistrement :
    // - en création : on ouvre la fiche du nouvel objet ;
    // - en édition : on reste sur la fiche et on rafraîchit l'entité
    //   (sans toggler le chargement, pour ne pas démonter le formulaire).
    const handleAfterSubmit = useCallback(
        (createdId?: number) => {
            if (isCreate) {
                if (createdId != null) {
                    navigate(catalogFichePath(createdId));
                }
                return;
            }
            if (!id) {
                return;
            }
            request(model.read({ id: String(id) }), {
                withToaster: false,
                successCallback: (result) => setEntity(result.data),
            });
        },
        [id, isCreate, model, navigate, request],
    );

    const title = isCreate
        ? buildCreationTitle("Création d'un catalogue", draftLabel)
        : (entity?.nomCommercial ?? 'Catalogue');

    const subtitleElements: FicheSubtitleElement[] =
        isCreate || !entity
            ? []
            : [
                  {
                      icon: domainIcon.props.icon,
                      label: entity.domaine?.libelle
                          ? `Domaine : ${entity.domaine.libelle}`
                          : undefined,
                  },
                  {
                      icon: offerIcon.props.icon,
                      label: (entity.offre as { libelle?: string } | undefined)?.libelle
                          ? `Offre : ${(entity.offre as { libelle?: string }).libelle}`
                          : undefined,
                      link: (entity.offre as { id?: number } | undefined)?.id
                          ? offerFichePath((entity.offre as { id?: number }).id as number)
                          : undefined,
                      linkTooltip: "Ouvrir l'offre",
                  },
                  {
                      icon: offerPackageIcon.props.icon,
                      label: (entity.package as { nomCommercial?: string } | null)?.nomCommercial
                          ? `Package : ${(entity.package as { nomCommercial?: string }).nomCommercial}`
                          : undefined,
                      link: (entity.package as { id?: number } | null)?.id
                          ? packageFichePath((entity.package as { id?: number }).id as number)
                          : undefined,
                      linkTooltip: 'Ouvrir le package',
                  },
                  {
                      icon: dateInput.props.icon,
                      label: formatPeriod(entity.dateDebut, entity.dateFin)
                          ? `Période : ${formatPeriod(entity.dateDebut, entity.dateFin)}`
                          : undefined,
                  },
                  {
                      icon: originIcon.props.icon,
                      label: (entity.origine as unknown as { origine?: string } | null)?.origine
                          ? `Origine : ${(entity.origine as unknown as { origine?: string }).origine}`
                          : undefined,
                  },
              ];

    const renderContent = () => {
        if (isLoading) {
            return <LoadingScreen />;
        }

        if (isInError || (!isCreate && !entity)) {
            return (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        minHeight: '30vh',
                    }}
                >
                    <EmptyBox
                        text="Catalogue introuvable"
                        subText="Ce catalogue n'existe pas ou n'a pas pu être chargé."
                    />
                </Box>
            );
        }

        return (
            <Form
                isCreate={isCreate}
                entity={entity}
                afterSubmit={handleAfterSubmit}
                onCancel={goToList}
                onLabelChange={setDraftLabel}
            />
        );
    };

    return (
        <Box sx={{ overflow: 'hidden' }}>
            <FicheHeader
                icon={catalogIcon}
                title={title}
                reference={isCreate ? undefined : entity?.id}
                subtitleElements={subtitleElements}
                sectionLabel={CATALOG_SECTION_LABEL}
                listPath={catalogListPath}
            />

            <Card sx={{ marginTop: 2, padding: 3 }}>{renderContent()}</Card>
        </Box>
    );
};

export default CatalogFiche;
